<template>
    <div
        class="sbs-scroll-wrapper"
        :class="scrollWrapperClasses"
        :style="contentStyles">
        <div
            ref="content"
            class="sbs-scroll-wrapper__content custom-scrollbar"
            :class="contentClasses"
            @scroll="contentScrollHandler">
            <!-- @slot Слот по-умолчанию -->
            <slot></slot>
        </div>

        <transition name="fade">
            <span
                v-if="isTopCurtainVisible"
                class="sbs-scroll-wrapper__curtain sbs-scroll-wrapper__curtain--top trs"
                :class="verticalCurtainClasses"
                :style="verticalCurtainStyles"></span>
        </transition>
        <transition name="fade">
            <span
                v-if="isLeftCurtainVisible"
                class="sbs-scroll-wrapper__curtain sbs-scroll-wrapper__curtain--left trs"
                :class="horizontalCurtainClasses"
                :style="horizontalCurtainStyles"></span>
        </transition>
        <transition name="fade">
            <span
                v-if="isBottomCurtainVisible"
                class="sbs-scroll-wrapper__curtain sbs-scroll-wrapper__curtain--bottom trs"
                :class="verticalCurtainClasses"
                :style="verticalCurtainStyles"></span>
        </transition>
        <transition name="fade">
            <span
                v-if="isRightCurtainVisible"
                class="sbs-scroll-wrapper__curtain sbs-scroll-wrapper__curtain--right trs"
                :class="horizontalCurtainClasses"
                :style="horizontalCurtainStyles"></span>
        </transition>
    </div>
</template>

<script>
import _throttle from 'lodash/throttle.js';
import ResizeObserver from '../../utils/resizeObserver.js';
import dimensionsMixin from '../../mixins/dimensions.js';
import {
    converToUnit,
    getScrollbarWidth,
} from '../../utils/helpers.js';
import { isArrayContainsValue } from '../../utils/validators.js';
import { AllColors } from '../../utils/colors.js';

const SCROLL_THROTTLE_MS = 100;

export default {
    name: 'SbsScrollWrapper',
    mixins: [dimensionsMixin],
    data() {
        return {
            contentBoundaries: {
                offsetHeight: null,
                offsetWidth: null,
                scrollHeight: null,
                scrollLeft: null,
                scrollTop: null,
                scrollWidth: null,
            },
            resizeObserver: null,
        };
    },
    computed: {
        isVerticalDirection() {
            return this.direction === 'vertical';
        },
        isHorizontalDirection() {
            return this.direction === 'horizontal';
        },
        scrollWrapperClasses() {
            return [
                {
                    'vertical-direction': this.isVerticalDirection,
                    'horizontal-direction': this.isHorizontalDirection,
                    'scroll-visible': this.isScrollVisible,
                },
            ];
        },
        computedFadeCurtainSize() {
            return converToUnit(this.fadeCurtainSize);
        },
        contentClasses() {
            return [this.contentClass];
        },
        contentStyles() {
            return { ...this.dimensionStyles };
        },
        verticalCurtainClasses() {
            const classes = [];

            if (this.fadeCurtainTheme) {
                classes.push(`bg-${this.fadeCurtainTheme}`);
            } else {
                classes.push(`bg-${AllColors.GRADIENT_WHITE_TOP}`);
            }

            return classes;
        },
        horizontalCurtainClasses() {
            const classes = [];

            if (this.fadeCurtainTheme) {
                classes.push(`bg-${this.fadeCurtainTheme}`);
            } else {
                classes.push(`bg-${AllColors.GRADIENT_WHITE_LEFT}`);
            }

            return classes;
        },
        verticalCurtainStyles() {
            return {
                height: this.computedFadeCurtainSize,
            };
        },
        horizontalCurtainStyles() {
            return {
                width: this.computedFadeCurtainSize,
            };
        },
        isTopCurtainVisible() {
            if (this.hideTopCurtain || this.isHorizontalDirection) return false;

            const {
                offsetHeight,
                scrollHeight,
                scrollTop,
            } = this.contentBoundaries;

            return this.hideCurtainOnReachEdge
                ? scrollTop > 0
                : offsetHeight < scrollHeight;
        },
        isLeftCurtainVisible() {
            if (this.hideLeftCurtain || this.isVerticalDirection) return false;

            const {
                offsetWidth,
                scrollLeft,
                scrollWidth,
            } = this.contentBoundaries;

            return this.hideCurtainOnReachEdge
                ? scrollLeft > 0
                : offsetWidth < scrollWidth;
        },
        isBottomCurtainVisible() {
            if (this.hideBottomCurtain || this.isHorizontalDirection) return false;

            const {
                offsetHeight,
                scrollHeight,
                scrollTop,
            } = this.contentBoundaries;

            return this.hideCurtainOnReachEdge
                ? offsetHeight + scrollTop < scrollHeight
                : offsetHeight < scrollHeight;
        },
        isRightCurtainVisible() {
            if (this.hideRightCurtain || this.isVerticalDirection) return false;

            const {
                offsetWidth,
                scrollLeft,
                scrollWidth,
            } = this.contentBoundaries;

            return this.hideCurtainOnReachEdge
                ? offsetWidth + scrollLeft < scrollWidth
                : offsetWidth < scrollWidth;
        },
        isScrollVisible() {
            const {
                offsetWidth,
                offsetHeight,
                scrollWidth,
                scrollHeight,
            } = this.contentBoundaries;

            return this.isVerticalDirection
                ? scrollHeight > offsetHeight
                : scrollWidth > offsetWidth;
        },
    },
    methods: {
        setScrollbarWidth() {
            const scrollbarWidth = getScrollbarWidth(this.$refs.content);

            this.$el.style.setProperty(
                '--wrapper-scrollbar-width',
                converToUnit(scrollbarWidth),
            );
        },
        calcContentBoundaries() {
            if (!this.$refs.content) return;

            const {
                offsetHeight,
                offsetWidth,
                scrollHeight,
                scrollLeft,
                scrollTop,
                scrollWidth,
            } = this.$refs.content;

            this.contentBoundaries.offsetHeight = offsetHeight;
            this.contentBoundaries.offsetWidth = offsetWidth;
            this.contentBoundaries.scrollHeight = scrollHeight;
            this.contentBoundaries.scrollLeft = scrollLeft;
            this.contentBoundaries.scrollTop = scrollTop;
            this.contentBoundaries.scrollWidth = scrollWidth;
        },
        contentScrollHandler(e) {
            /**
             * Эмит события `scroll`, срабатывает при скролле контента.
             * В качестве параметра объект события
             */
            this.$emit('scroll', e);
            this.calcContentBoundaries();
        },
        contentResizeHandler() {
            this.setScrollbarWidth();
            this.calcContentBoundaries();
        },
        emitScrollReachStartEvent() {
            /**
             * Эмит события `scroll:reachStart`, срабатывает при скролле до начала контента
             */
            this.$emit('scroll:reachStart');
        },
        emitScrollReachEndEvent() {
            /**
             * Эмит события `scroll:reachEnd`, срабатывает при скролле до конца контента
             */
            this.$emit('scroll:reachEnd');
        },
    },
    props: {
        /**
         * Класс для блока с контентом
         */
        contentClass: {
            type: String,
            default: '',
        },
        /**
         * Определяет направление скролла для блока контента
         */
        direction: {
            type: String,
            default: 'vertical',
            validator: isArrayContainsValue(['vertical', 'horizontal']),
        },
        /**
         * Ширина/высота шторок затухания
         */
        fadeCurtainSize: {
            type: [Number, String],
            default: 75,
        },
        /**
         * Тема (цветовые решения) для цвета фона для шторок затухания
         */
        fadeCurtainTheme: {
            type: String,
            default: '',
        },
        /**
         * Включает исчезновение шторок затухания сверу и снизу при достижении границы блока
         */
        hideCurtainOnReachEdge: {
            type: Boolean,
            default: true,
        },
        /**
         * Всегда скрывать нижнюю шторку затухания
         */
        hideBottomCurtain: {
            type: Boolean,
            default: false,
        },
        /**
         * Всегда скрывать левую шторку затухания
         */
        hideLeftCurtain: {
            type: Boolean,
            default: false,
        },
        /**
         * Всегда скрывать правую шторку затухания
         */
        hideRightCurtain: {
            type: Boolean,
            default: false,
        },
        /**
         * Всегда скрывать верхнюю шторку затухания
         */
        hideTopCurtain: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        contentBoundaries: {
            deep: true,
            handler() {
                const {
                    offsetHeight,
                    scrollHeight,
                    scrollTop,
                    offsetWidth,
                    scrollLeft,
                    scrollWidth,
                } = this.contentBoundaries;

                if (this.isVerticalDirection) {
                    if (scrollTop === 0) {
                        this.emitScrollReachStartEvent();
                    }

                    if (offsetHeight + scrollTop === scrollHeight) {
                        this.emitScrollReachEndEvent();
                    }
                } else if (this.isHorizontalDirection) {
                    if (scrollLeft === 0) {
                        this.emitScrollReachStartEvent();
                    }

                    if (offsetWidth + scrollLeft === scrollWidth) {
                        this.emitScrollReachEndEvent();
                    }
                }
            },
        },
    },
    created() {
        this.resizeObserver = new ResizeObserver(this.contentResizeHandler);
        this.contentScrollHandler = _throttle(
            this.contentScrollHandler,
            SCROLL_THROTTLE_MS,
        );
    },
    async mounted() {
        this.setScrollbarWidth();
        await this.$nextTick();

        if (this.$refs.content) {
            this.resizeObserver.observe(this.$refs.content);
        }
    },
    beforeUnmount() {
        this.resizeObserver.disconnect();
    },
};
</script>

<style lang="scss">
.sbs-scroll-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &__content {
        position: relative;
        display: flex;
        -webkit-overflow-scrolling: touch;

        .sbs-scroll-wrapper.vertical-direction & {
            flex-direction: column;
            overflow-x: hidden;
            overflow-y: auto;
        }

        .sbs-scroll-wrapper.horizontal-direction & {
            flex-direction: row;
            overflow-x: auto;
            overflow-y: hidden;
        }

        & > * {
            flex-shrink: 0;
        }
    }

    &__curtain {
        position: absolute;
        z-index: 10;
        pointer-events: none;

        .sbs-scroll-wrapper.vertical-direction & {
            right: var(--wrapper-scrollbar-width);
            left: 0;

            &--top {
                top: 0;
            }

            &--bottom {
                bottom: 0;
                transform: rotate(180deg);
            }
        }

        .sbs-scroll-wrapper.horizontal-direction & {
            top: 0;
            bottom: var(--wrapper-scrollbar-width);

            &--left {
                left: 0;
                transform: rotate(180deg);
            }

            &--right {
                right: 0;
            }
        }
    }
}
</style>
